import { useEffect, useRef } from 'react';
import type * as ThreeNS from 'three';

import type { HeroProduct } from './heroProducts';

type ThreeModule = typeof ThreeNS;

/* ------------------------------------------------------------------ *
 * Scene constants
 * ------------------------------------------------------------------ */

/** Product cards are painted into an offscreen 2D canvas at this size, then used as a texture. */
const CARD_PX_W = 640;
const CARD_PX_H = 480;
/** 4:3, matching the static grid's `aspect-4/3` tiles. */
const CARD_ASPECT = CARD_PX_H / CARD_PX_W;

const CAMERA_FOV = 42;
const CAMERA_DIST = 10;

/** Gutter between the two columns, and the margin outside them, as fractions of card width. */
const GAP_RATIO = 0.09;
const EDGE_RATIO = 0.14;
/** Rows kept in view — the height budget each card gets. */
const VISIBLE_ROWS = 3;

/** World units per second. The two columns differ slightly so they never feel geared together. */
const COLUMN_SPEED = [0.6, 0.52];
/** Left column travels up, right column travels down. */
const COLUMN_DIRECTION = [1, -1];
/** Half-pitch stagger, so the two columns never line up into rows. */
const COLUMN_PHASE = [0, 0.5];

/** Height of the soft top/bottom cut, as a share of the panel. Cards dissolve rather than clip. */
const EDGE_MASK = 'linear-gradient(to bottom, transparent 0%, #000 16%, #000 84%, transparent 100%)';

const SECONDARY = 0x151515;

/* ------------------------------------------------------------------ *
 * Canvas painting helpers
 * ------------------------------------------------------------------ */

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

/** Draws `img` centred on (cx, cy), scaled to fit a `box`-sized square without distortion. */
function drawContain(ctx: CanvasRenderingContext2D, img: HTMLImageElement, cx: number, cy: number, box: number) {
    const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
    let w = box;
    let h = box / ratio;
    if (h > box) {
        h = box;
        w = box * ratio;
    }
    ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
}

const LABEL_FONT = '"Plus Jakarta Sans", system-ui, -apple-system, sans-serif';
const LABEL_MAX_W = 540;

/**
 * Paints one product card, matching the static grid's tile: soft white panel, grey logo, and a
 * `Podus <Name>` label. `active` is the hover state — it fades in over the idle card, pixel-aligned,
 * so only the fill, logo colour and border change.
 */
function paintCard(product: HeroProduct, logo: HTMLImageElement | null, active: boolean): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = CARD_PX_W;
    canvas.height = CARD_PX_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    // Canvas stores a fully transparent pixel as transparent *black*, and mipmap filtering later
    // averages that black into the tile's edge — a dark halo that reads as a drop shadow. Priming
    // the sheet with all-but-invisible white keeps the padding the card's own colour, so the
    // filtered edge stays neutral. (Alpha must round to at least 1/255 to survive the canvas.)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.01)';
    ctx.fillRect(0, 0, CARD_PX_W, CARD_PX_H);

    const pad = 20;
    const w = CARD_PX_W - pad * 2;
    const h = CARD_PX_H - pad * 2;

    ctx.fillStyle = active ? '#ffffff' : 'rgba(255, 255, 255, 0.86)';
    roundedRectPath(ctx, pad, pad, w, h, 46);
    ctx.fill();

    ctx.strokeStyle = active ? '#151515' : 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 3;
    roundedRectPath(ctx, pad, pad, w, h, 46);
    ctx.stroke();

    if (logo) {
        ctx.save();
        if (!active) {
            ctx.filter = 'grayscale(1)';
            ctx.globalAlpha = 0.72;
        }
        drawContain(ctx, logo, CARD_PX_W / 2, 192, 186);
        ctx.restore();
    }

    // Shrink the label if a long product name would crowd the card edges.
    const prefix = `${product.prefix} `;
    let size = 54;
    let prefixW = 0;
    let nameW = 0;
    for (; size > 34; size -= 2) {
        ctx.font = `600 ${size}px ${LABEL_FONT}`;
        prefixW = ctx.measureText(prefix).width;
        ctx.font = `800 ${size}px ${LABEL_FONT}`;
        nameW = ctx.measureText(product.name).width;
        if (prefixW + nameW <= LABEL_MAX_W) break;
    }

    let x = (CARD_PX_W - (prefixW + nameW)) / 2;
    const baseline = 372;
    ctx.textBaseline = 'alphabetic';
    ctx.font = `600 ${size}px ${LABEL_FONT}`;
    ctx.fillStyle = active ? 'rgba(21, 21, 21, 0.92)' : 'rgba(21, 21, 21, 0.72)';
    ctx.fillText(prefix, x, baseline);
    x += prefixW;
    ctx.font = `800 ${size}px ${LABEL_FONT}`;
    ctx.fillStyle = '#151515';
    ctx.fillText(product.name, x, baseline);

    if (product.comingSoon) {
        const badge = 'Coming Soon';
        ctx.font = `700 24px ${LABEL_FONT}`;
        const badgeW = ctx.measureText(badge).width + 36;
        const badgeH = 44;
        const bx = CARD_PX_W - pad - 24 - badgeW;
        const by = pad + 24;
        ctx.fillStyle = active ? 'rgba(255, 102, 0, 0.2)' : 'rgba(255, 102, 0, 0.12)';
        roundedRectPath(ctx, bx, by, badgeW, badgeH, badgeH / 2);
        ctx.fill();
        ctx.fillStyle = '#ff6600';
        ctx.textBaseline = 'middle';
        ctx.fillText(badge, bx + 18, by + badgeH / 2 + 1);
    }

    return canvas;
}

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

interface ProductMarquee3DProps {
    products: HeroProduct[];
    /** Called when WebGL, `three`, or the GL context goes away, so the caller can swap in the grid. */
    onUnavailable?: () => void;
}

interface MarqueeCard {
    group: ThreeNS.Group;
    base: ThreeNS.Mesh;
    active: ThreeNS.Mesh;
    baseMat: ThreeNS.MeshBasicMaterial;
    activeMat: ThreeNS.MeshBasicMaterial;
    /** Which conveyor this card rides. */
    column: number;
    /** Slot index within the column, converted to a world offset once the layout is known. */
    slot: number;
    /** Index into `products`, so a hit can be turned back into a link. */
    product: number;
    hover: number;
    /** 1 while the card is clear of the masked edges — gates hit-testing, not opacity. */
    live: number;
    delay: number;
}

const easeOutCubic = (v: number) => 1 - Math.pow(1 - v, 3);
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** Wraps `value` into [-span/2, span/2) — the seam that makes each column endless. */
const wrap = (value: number, span: number) => ((((value + span / 2) % span) + span) % span) - span / 2;

/**
 * The hero's product tiles as two endless 3D columns: every product appears in both, the left one
 * travelling up and the right one down. Cards move in a straight line at a constant size and pitch;
 * a soft mask dissolves them at the top and bottom rather than clipping them.
 *
 * Cards are billboard-free canvas textures — the type stays crisp and identical to the static grid.
 * `three` is imported dynamically, so it never lands in the initial bundle and visitors who fall
 * back to the grid never download it.
 */
export default function ProductMarquee3D({ products, onUnavailable }: ProductMarquee3DProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const unavailableRef = useRef(onUnavailable);
    unavailableRef.current = onUnavailable;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let disposed = false;
        let teardown: (() => void) | null = null;

        const build = async () => {
            let THREE: ThreeModule;
            try {
                THREE = await import('three');
            } catch {
                if (!disposed) unavailableRef.current?.();
                return;
            }
            if (disposed) return;

            // Labels are painted into a canvas, so the webfont has to be resolved before the first paint.
            try {
                await document.fonts.ready;
            } catch {
                /* FontFaceSet unsupported — falls back to the system stack */
            }

            const logos = await Promise.all(products.map((product) => loadImage(product.logo)));
            if (disposed) return;

            let renderer: ThreeNS.WebGLRenderer;
            try {
                renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
            } catch {
                unavailableRef.current?.();
                return;
            }

            const dom = renderer.domElement;
            renderer.setClearColor(0x000000, 0);
            dom.style.display = 'block';
            dom.style.width = '100%';
            dom.style.height = '100%';
            dom.style.opacity = '0';
            dom.style.transition = 'opacity 900ms ease';
            dom.style.touchAction = 'pan-y';
            dom.style.maskImage = EDGE_MASK;
            dom.style.webkitMaskImage = EDGE_MASK;
            container.appendChild(dom);

            const scene = new THREE.Scene();
            const camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, 0.1, 100);
            camera.position.set(0, 0, CAMERA_DIST);
            const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

            const texture = (canvas: HTMLCanvasElement) => {
                const tex = new THREE.CanvasTexture(canvas);
                tex.colorSpace = THREE.SRGBColorSpace;
                tex.anisotropy = maxAnisotropy;
                tex.needsUpdate = true;
                return tex;
            };

            // Unit-width geometry: the whole card group is scaled to the layout's card width.
            const cardGeo = new THREE.PlaneGeometry(1, CARD_ASPECT);
            const overlay = { transparent: true, depthTest: false, depthWrite: false, toneMapped: false } as const;

            const root = new THREE.Group();
            scene.add(root);

            /* --- ambient dust, for depth ------------------------------------ */
            const dustCount = 90;
            const dustPos = new Float32Array(dustCount * 3);
            for (let i = 0; i < dustCount; i += 1) {
                dustPos[i * 3] = (Math.random() - 0.5) * 11;
                dustPos[i * 3 + 1] = (Math.random() - 0.5) * 9;
                dustPos[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
            }
            const dustGeo = new THREE.BufferGeometry();
            dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
            const dustMat = new THREE.PointsMaterial({
                color: SECONDARY,
                size: 0.035,
                sizeAttenuation: true,
                transparent: true,
                opacity: 0,
                depthWrite: false,
            });
            const dust = new THREE.Points(dustGeo, dustMat);
            dust.renderOrder = 1;
            root.add(dust);

            /* --- the two conveyors -------------------------------------------- */
            const columns = [new THREE.Group(), new THREE.Group()];
            columns.forEach((column) => root.add(column));

            // Both columns list every product. The right one runs the list backwards, so the two
            // sides can never drift into showing the same product on the same row.
            const cards: MarqueeCard[] = [];
            columns.forEach((column, columnIndex) => {
                for (let slot = 0; slot < products.length; slot += 1) {
                    const productIndex = columnIndex === 0 ? slot : products.length - 1 - slot;
                    const product = products[productIndex];

                    const baseMat = new THREE.MeshBasicMaterial({
                        map: texture(paintCard(product, logos[productIndex], false)),
                        opacity: 0,
                        ...overlay,
                    });
                    const activeMat = new THREE.MeshBasicMaterial({
                        map: texture(paintCard(product, logos[productIndex], true)),
                        opacity: 0,
                        ...overlay,
                    });
                    const base = new THREE.Mesh(cardGeo, baseMat);
                    const active = new THREE.Mesh(cardGeo, activeMat);
                    active.position.z = 0.003;

                    const group = new THREE.Group();
                    group.add(base, active);
                    column.add(group);

                    const card: MarqueeCard = {
                        group,
                        base,
                        active,
                        baseMat,
                        activeMat,
                        column: columnIndex,
                        slot,
                        product: productIndex,
                        hover: 0,
                        live: 0,
                        delay: slot * 0.07,
                    };
                    base.userData.card = cards.length;
                    cards.push(card);
                }
            });
            const hitMeshes = cards.map((card) => card.base);

            /* --- layout ----------------------------------------------------------
             * Card width is whichever is tighter: two columns across the frame, or `VISIBLE_ROWS`
             * rows down it. Everything else — pitch, loop length, fade band — follows from that, so
             * the same scene fills a phone-width panel and a desktop column without special cases.
             */
            let cardW = 1;
            let cardH = CARD_ASPECT;
            let pitch = 1;
            let loop = products.length;
            /** Beyond this the card is off-frame entirely and can be skipped. */
            let cullSpan = 1;
            /** Inside this the card is clear of the mask, so it can be hovered and clicked. */
            let hitSpan = 1;

            const fit = () => {
                const w = container.clientWidth || 1;
                const h = container.clientHeight || 1;
                renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
                renderer.setSize(w, h, false);
                camera.aspect = w / h;
                camera.updateProjectionMatrix();

                const viewHalfH = CAMERA_DIST * Math.tan((CAMERA_FOV * Math.PI) / 180 / 2);
                const viewHalfW = viewHalfH * camera.aspect;

                cardW = Math.min(
                    (2 * viewHalfW) / (2 + GAP_RATIO + 2 * EDGE_RATIO),
                    (2 * viewHalfH) / (VISIBLE_ROWS * (CARD_ASPECT + GAP_RATIO)),
                );
                cardH = cardW * CARD_ASPECT;
                const gap = cardW * GAP_RATIO;
                pitch = cardH + gap;
                loop = pitch * products.length;

                // Travel is straight, so distance along the column is world Y directly.
                cullSpan = viewHalfH + cardH * 1.2;
                hitSpan = viewHalfH * 0.84;

                const columnX = (cardW + gap) / 2;
                columns[0].position.x = -columnX;
                columns[1].position.x = columnX;
            };
            fit();
            const resizeObserver = new ResizeObserver(fit);
            resizeObserver.observe(container);

            /* --- interaction ---------------------------------------------------- */
            const raycaster = new THREE.Raycaster();
            const pointer = new THREE.Vector2();
            const parallax = new THREE.Vector2();
            const parallaxTarget = new THREE.Vector2();
            let pointerInside = false;
            let hovered = -1;

            const updatePointer = (event: PointerEvent) => {
                const rect = dom.getBoundingClientRect();
                if (!rect.width || !rect.height) return;
                const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
                const ny = -(((event.clientY - rect.top) / rect.height) * 2 - 1);
                pointer.set(nx, ny);
                parallaxTarget.set(nx, ny);
                pointerInside = true;
            };
            const onPointerLeave = () => {
                pointerInside = false;
                hovered = -1;
                parallaxTarget.set(0, 0);
                dom.style.cursor = '';
            };
            const onClick = () => {
                if (hovered < 0) return;
                const product = products[cards[hovered].product];
                if (!product.comingSoon) window.open(product.href, '_blank', 'noopener,noreferrer');
            };
            const onContextLost = (event: Event) => {
                event.preventDefault();
                unavailableRef.current?.();
            };

            dom.addEventListener('pointermove', updatePointer);
            dom.addEventListener('pointerdown', updatePointer);
            dom.addEventListener('pointerleave', onPointerLeave);
            dom.addEventListener('click', onClick);
            dom.addEventListener('webglcontextlost', onContextLost);

            /* --- animation ------------------------------------------------------- */
            const clock = new THREE.Clock();
            const scroll = [0, 0];
            const speed = [COLUMN_SPEED[0], COLUMN_SPEED[1]];
            let intro = 0;
            let revealed = false;

            const tick = () => {
                const dt = Math.min(clock.getDelta(), 0.05);
                const time = clock.elapsedTime;
                intro = Math.min(1, intro + dt / 1.4);

                if (pointerInside) {
                    raycaster.setFromCamera(pointer, camera);
                    const hits = raycaster.intersectObjects(hitMeshes, false);
                    // Faded cards are on their way out of frame — they should not be clickable.
                    const hit = hits.find((entry) => cards[entry.object.userData.card as number].live > 0.4);
                    const next = hit ? (hit.object.userData.card as number) : -1;
                    if (next !== hovered) {
                        hovered = next;
                        const product = hovered >= 0 ? products[cards[hovered].product] : null;
                        dom.style.cursor = product && !product.comingSoon ? 'pointer' : '';
                    }
                }

                const hoveredColumn = hovered >= 0 ? cards[hovered].column : -1;
                for (let c = 0; c < columns.length; c += 1) {
                    const target = hoveredColumn === c ? COLUMN_SPEED[c] * 0.08 : COLUMN_SPEED[c];
                    speed[c] += (target - speed[c]) * Math.min(1, dt * 3);
                    scroll[c] += speed[c] * COLUMN_DIRECTION[c] * dt;
                }

                parallax.x += (parallaxTarget.x - parallax.x) * Math.min(1, dt * 3);
                parallax.y += (parallaxTarget.y - parallax.y) * Math.min(1, dt * 3);
                root.rotation.y = parallax.x * 0.05;
                root.rotation.x = -parallax.y * 0.03;

                for (let i = 0; i < cards.length; i += 1) {
                    const card = cards[i];
                    const enter = easeOutCubic(clamp((intro - card.delay) / (1 - card.delay), 0, 1));

                    // Position along the conveyor, wrapped so each column is endless.
                    const s = wrap((card.slot + COLUMN_PHASE[card.column]) * pitch + scroll[card.column], loop);

                    card.hover += ((hovered === i ? 1 : 0) - card.hover) * Math.min(1, dt * 9);
                    card.live = clamp((hitSpan - Math.abs(s)) / (cardH * 0.6), 0, 1) * enter;

                    const breathe = Math.sin(time * 0.7 + card.slot * 1.3 + card.column * 2.1) * 0.02;
                    // Straight travel: even spacing and one size the whole way up. Depth is reserved
                    // for the hover pop and the entry drift, so nothing shifts as a card scrolls.
                    card.group.position.set(0, s, (breathe - (1 - enter) * 1.4) * cardW);
                    card.group.scale.setScalar(cardW);

                    card.group.visible = Math.abs(s) < cullSpan;
                    card.baseMat.opacity = enter;
                    card.activeMat.opacity = enter * card.hover;

                    // Depth testing is off, so draw order stands in for it. Only the entry drift
                    // moves a card in Z now, but cards enter staggered and must not punch through.
                    const order = 10 + Math.round((card.group.position.z + 6) * 20) * 2;
                    card.base.renderOrder = order;
                    card.active.renderOrder = order + 1;
                }

                dust.rotation.z = time * 0.01;
                dustMat.opacity = 0.12 * easeOutCubic(intro);

                renderer.render(scene, camera);

                if (!revealed) {
                    revealed = true;
                    dom.style.opacity = '1';
                }
            };

            /* --- run only while visible ------------------------------------------- */
            let running = false;
            let onScreen = false;
            const sync = () => {
                const shouldRun = onScreen && document.visibilityState !== 'hidden';
                if (shouldRun === running) return;
                running = shouldRun;
                renderer.setAnimationLoop(shouldRun ? tick : null);
            };
            const intersectionObserver = new IntersectionObserver(
                ([entry]) => {
                    onScreen = entry.isIntersecting;
                    sync();
                },
                { threshold: 0 },
            );
            intersectionObserver.observe(container);
            const onVisibility = () => sync();
            document.addEventListener('visibilitychange', onVisibility);

            teardown = () => {
                renderer.setAnimationLoop(null);
                intersectionObserver.disconnect();
                resizeObserver.disconnect();
                document.removeEventListener('visibilitychange', onVisibility);
                dom.removeEventListener('pointermove', updatePointer);
                dom.removeEventListener('pointerdown', updatePointer);
                dom.removeEventListener('pointerleave', onPointerLeave);
                dom.removeEventListener('click', onClick);
                dom.removeEventListener('webglcontextlost', onContextLost);

                cards.forEach((card) => {
                    card.baseMat.map?.dispose();
                    card.baseMat.dispose();
                    card.activeMat.map?.dispose();
                    card.activeMat.dispose();
                });
                [cardGeo, dustGeo].forEach((geo) => geo.dispose());
                dustMat.dispose();
                renderer.dispose();
                dom.remove();
            };
        };

        void build();

        return () => {
            disposed = true;
            teardown?.();
        };
    }, [products]);

    return <div ref={containerRef} aria-hidden="true" className="h-full w-full" />;
}
